
import { useEffect, useMemo } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/react-hooks';
import { SEARCH_QUERY } from '../../queries/SEARCH_QUERY';
import { useSanitizer } from '../../../components/common/learningItem/sanitizers';
import { USER_DETAILS_QUERY } from '../../queries/USER_DETAILS_QUERY';
import { useTranslations } from '../../../hooks/translations';
import { READ_NOTIFICATIONS_QUERY } from '../../queries/READ_NOTIFICATIONS_QUERY';
import { READ_NOTIFICAITON_MUTATION } from '../../mutations/READ_NOTIFICATION_MUTATION';
import { authService } from '../../../services';

export function useNotificationsQuery( options ) {
    const { data: userData, loading: userDataLoading } = useQuery( USER_DETAILS_QUERY );
    const { data: notiRead } = useQuery( READ_NOTIFICATIONS_QUERY );
    const [ query, { data: rawData, loading, error, refetch } ] = useLazyQuery( SEARCH_QUERY, {
        ...options,
        fetchPolicy: 'network-only',
    } );
    const [ readQuery ] = useMutation( READ_NOTIFICAITON_MUTATION );
    const { locale } = useTranslations();

    const { sanitizeNotifications } = useSanitizer();

    const refineSearchData = ( data ) => {
        if ( !data?.search?.edges || !notiRead?.readNotifications?.edges ) {return [];}

        const readIds = notiRead.readNotifications.edges.map( e => e.node.notificationId );

        return sanitizeNotifications( data.search.edges )
            .map( n => ( { ...n, isRead: readIds.includes( n.id ) } ) )
            .sort( ( a, b ) => new Date( a.publishedAt ) - new Date( b.publishedAt ) );
    };

    function markNotificationRead( id ) {
        readQuery( {
            variables: {
                input: {
                    personId: authService.isAdminUser() ? undefined : userData.person.id, 
                    notificationId: id
                }
            }
        } );
    }

    useEffect( () => {
        if ( !userDataLoading ) {
            const formattedLocale = locale?.replace( '-', '_' );

            query( {
                variables: {
                    types: [ 'notifications' ],
                    localeCode: formattedLocale,
                    limit: 1000,
                    personId: authService.isAdminUser() ? undefined : userData?.person.id,
                }
            } );
        }
    }, [ userDataLoading, locale ] );

    return useMemo ( () => {

        let pageData = {
            totalResults: rawData?.search.totalCount,
            ...rawData?.search.searchOptions,
        };
        let refinedData = refineSearchData( rawData || [] );
        // dont segment the data because its already segmented on the backend

        return {
            error: ( error ),
            loading: loading,
            data: refinedData,
            pageData,
            refetch,
            markNotificationRead,
        };
    }, [ error, loading, rawData, refetch, sanitizeNotifications, notiRead, locale ] );
}
